import { Component, ChangeDetectionStrategy, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SystemHealthService, UserStats } from '@services/system-health.service';
import { SystemStatus, ModuleStatus, LocalizationMap } from '@models/system-health.models';
import { UserTicketService } from '@6grain/masofa-monolith-api-client/common';
import { buildLanguageCandidates, findLocalizationValue, resolveLocalizedName, humanizeName, toLocalizationMap } from '@app/utils/language-candidates.util';

@Component({
  selector: 'app-electronic-field-map-summary',
  standalone: true,
  imports: [CommonModule, TranslateModule, ToggleSwitchModule, FormsModule, TooltipModule],
  templateUrl: './electronic-field-map-summary.component.html',
  styleUrl: './electronic-field-map-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ElectronicFieldMapSummaryComponent implements OnInit {
  isAdmin: boolean = true;

  systemStatus: SystemStatus | null = null;
  loading: boolean = true;
  error: string | null = null;

  userStats: UserStats = {
    total: 0,
    active: 0,
    blocked: 0
  };

  appealsCount: number = 0;

  private systemHealthService = inject(SystemHealthService);
  private userTicketService = inject(UserTicketService);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.loadSystemStatus();
    this.loadUserStats();
    this.loadAppealsCount();
  }

  private loadSystemStatus(): void {
    this.loading = true;
    this.error = null;
    this.cdr.markForCheck();

    this.systemHealthService.getSystemStatus().subscribe({
      next: (systemStatus) => {
        this.systemStatus = systemStatus;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading system status:', err);
        this.error = 'Failed to load system status';
        this.systemStatus = null;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private loadUserStats(): void {
    this.systemHealthService.getUserStats().subscribe({
      next: (stats) => {
        this.userStats = stats;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading user stats:', err);
        this.userStats = { total: 0, active: 0, blocked: 0 };
        this.cdr.markForCheck();
      }
    });
  }

  private loadAppealsCount(): void {
    this.userTicketService.commonUserTicketGetTotalCountPost({}).subscribe({
      next: (count) => {
        this.appealsCount = count || 0;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading appeals count:', err);
        this.appealsCount = 0;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Получает CSS класс для статуса
   */
  getStatusClass(status: 'healthy' | 'warning' | 'error'): string {
    switch (status) {
      case 'healthy': return 'status-healthy';
      case 'warning': return 'status-warning';
      case 'error': return 'status-error';
      default: return 'status-unknown';
    }
  }

  /**
   * Получает текст статуса для отображения
   */
  getStatusText(status: 'healthy' | 'warning' | 'error'): string {
    switch (status) {
      case 'healthy': return 'Normal';
      case 'warning': return 'Warning';
      case 'error': return 'Errors';
      default: return 'Unknown';
    }
  }

  /**
   * Получает иконку для статуса
   */
  getStatusIcon(status: 'healthy' | 'warning' | 'error'): string {
    switch (status) {
      case 'healthy': return '../../../assets/icons/main/light/check-circle.svg';
      case 'warning': return '../../../assets/icons/main/light/alert-line.svg';
      case 'error': return '../../../assets/icons/main/light/alert-fill.svg';
      default: return '../../../assets/icons/main/light/alert-line.svg';
    }
  }

  /**
   * Получает локализованное название модуля
   */
  getModuleDisplayName(module: ModuleStatus): string {
    return this.resolveLocalizedName(this.getModuleLocalization(module), this.getModuleFallbackName(module));
  }

  /**
   * Обрезает текст для отображения в сводке
   */
  truncateText(text: string, maxLength: number = 18): string {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  }

  /**
   * Переход к управлению пользователями
   */
  goToUserManagement(): void {
    this.router.navigate(['/admin/user-management']);
  }

  /**
   * Переход к управлению appeals
   */
  goToAppealsManagement(): void {
    this.router.navigate(['/admin/appeals']);
  }

  /**
   * Переход к детальному мониторингу системы
   */
  goToSystemMonitoring(): void {
    this.router.navigate(['/admin/system-status']);
  }

  /**
   * Обновление данных
   */
  refreshData(): void {
    this.loadSystemStatus();
    this.loadUserStats();
    this.loadAppealsCount();
  }

  private getModuleLocalization(module: ModuleStatus): LocalizationMap | null {
    const raw = (module as any).moduleNames ?? module.moduleNames;
    return toLocalizationMap(raw);
  }

  private getModuleFallbackName(module: ModuleStatus): string {
    const raw = module.name || 'Unknown Module';
    return humanizeName(raw);
  }

  private resolveLocalizedName(localization: LocalizationMap | null | undefined, fallback: string): string {
    return resolveLocalizedName(localization, fallback, this.translateService);
  }

  private findLocalizationValue(localization: LocalizationMap, key: string): string | undefined {
    return findLocalizationValue(localization, key);
  }

  private getLanguageCandidates(): string[] {
    return buildLanguageCandidates(this.translateService);
  }

  private humanizeName(value: string): string {
    return humanizeName(value);
  }

  private toLocalizationMap(value: any): LocalizationMap | null {
    return toLocalizationMap(value);
  }
}
